import React, { useMemo, useEffect, useCallback, useRef } from 'react';
import { toast } from 'react-toastify';
import InvoiceRenderer from '../../../../invoice/InvoiceRenderer';
import { BaseInvoiceDTO, CreateInvoiceDTO, EditInvoiceValues, ProjectData } from '../../../../invoice/invoice.type';
import { useMonitoringReportShared } from '../../context/MonitoringReportContext';
import { ReportInvoiceDTO } from '../../types';
import { exportInvoiceAsPDF, exportAllInvoicesAsPDF } from '../../../../invoice/invoice.util';
import { saveLimitedReportInvoice, getLimitedReport, updateLimitedReportCurrentInvoice } from '../../../limited-report/limited-report.api';
import { saveMagicReportInvoice, getMagicReport, updateMagicReportInvoice } from '../../../magic-report/magic-report.api';
import type { CreateLimitedReportInvoiceDTO } from '../../../limited-report/limited-report.type';
import type { CreateMagicReportInvoiceDTO } from '../../../magic-report/magic-report.type';
import { usePrompt } from '../../../../AppPrompt';
import { SectionCompleteToggle } from '../../components/SectionCompleteToggle';
import { useCompletionToggleEffect } from '../../../../../hooks/useCompletionToggleEffect';

export interface InvoiceStepProps {
  title?: string;
  service?: string;
  mode?: 'current' | 'past' | 'regular';
  showExportAll?: boolean;
}

const InvoiceStep: React.FC<InvoiceStepProps> = ({ title = 'Invoices', service = '', mode = 'current', showExportAll = true }) => {
  const { reportState, setReportState, setIsDirty, reportType, navigationGuardRef, saveReport } = useMonitoringReportShared() as any;
  const { projectSummary, invoices = [], invoiceData, siteConditions } = reportState;
  const { getConfirmation } = usePrompt();
  const invoiceSaveRef = useRef<(() => Promise<boolean>) | null>(null);

  const isMagicReport = reportType === 'magic';
  const isInvoiceStepCompleted = Boolean(invoiceData?.isCompleted);
  const handleToggleInvoiceStepCompleted = useCallback(() => {
    setIsDirty(true);
    setReportState((prev: any) => ({
      ...prev,
      invoiceData: { ...(prev.invoiceData || {}), isCompleted: !prev.invoiceData?.isCompleted },
    }));
  }, [setReportState, setIsDirty]);

  const { isSavingCompletion } = useCompletionToggleEffect({
    isCompleted: isInvoiceStepCompleted,
    enabled: isMagicReport,
    saveReport,
  });

  const projectData: ProjectData | undefined = useMemo(() => {
    if (!projectSummary) return undefined;
    return {
      projectNumber: projectSummary.projectNumber,
      projectTitle: projectSummary.projectTitle,
      clientName: projectSummary.client?.name,
      clientAddress: projectSummary.client?.address,
      projectAddress: projectSummary.projectAddress,
      invoices: invoices as unknown as BaseInvoiceDTO[],
      loanOfficerName: projectSummary.loanOfficerName || undefined,
      siteVisitNumber: siteConditions?.siteVisit || undefined,
      monitoringFee: parseFloat(projectSummary.monitoringFee || '') || undefined,
    };
  }, [projectSummary, invoices, siteConditions]);

  const handleAddInvoice = async (invoiceData: CreateInvoiceDTO) => {
    try {
      const isCurrentInvoice = (invoiceData as any).currentInvoice;
      const reportId = reportState.reportId || '';

      if (!reportId) {
        toast.error('Report ID is missing. Cannot save invoice.');
        return;
      }

      // Determine report type from service prop
      const isLimitedReport = service?.includes('Limited');
      const isMagicReport = service?.includes('Magic');

      // Check if we're updating an existing current invoice
      const existingCurrentInvoice = invoices.find((inv: ReportInvoiceDTO) => inv.currentInvoice);
      const isUpdatingCurrentInvoice = isCurrentInvoice && existingCurrentInvoice;

      // Update existing current invoice or create new invoice
      if (isUpdatingCurrentInvoice) {
        // Update existing current invoice
        if (isLimitedReport) {
          const invoiceDTO: CreateLimitedReportInvoiceDTO = {
            limitedReportId: reportId,
            invoice: {
              number: invoiceData.invoice.number,
              invoiceAmount: invoiceData.invoice.invoiceAmount,
              invoiceDate: invoiceData.invoice.invoiceDate,
              paidAmount: invoiceData.invoice.paidAmount,
              comment: invoiceData.invoice.comment,
            },
            recepient: invoiceData.recepient,
            recepientAddress: invoiceData.recepientAddress,
            projectName: invoiceData.projectName,
            projectAddress: invoiceData.projectAddress,
            service: invoiceData.service,
            currentInvoice: isCurrentInvoice,
          };
          await updateLimitedReportCurrentInvoice(reportId, existingCurrentInvoice.id, invoiceDTO);
        } else {
          const invoiceDTO: CreateMagicReportInvoiceDTO = {
            magicReportId: reportId,
            invoice: {
              number: invoiceData.invoice.number,
              invoiceAmount: invoiceData.invoice.invoiceAmount,
              invoiceDate: invoiceData.invoice.invoiceDate,
              paidAmount: invoiceData.invoice.paidAmount,
              comment: invoiceData.invoice.comment,
            },
            recepient: invoiceData.recepient,
            recepientAddress: invoiceData.recepientAddress,
            projectName: invoiceData.projectName,
            projectAddress: invoiceData.projectAddress,
            service: invoiceData.service,
            currentInvoice: isCurrentInvoice,
          };
          await updateMagicReportInvoice(reportId, existingCurrentInvoice.id, invoiceDTO);
        }
      } else {
        // Create new invoice (past invoice or first current invoice)
        if (isLimitedReport) {
          const invoiceDTO: CreateLimitedReportInvoiceDTO = {
            limitedReportId: reportId,
            invoice: {
              number: invoiceData.invoice.number,
              invoiceAmount: invoiceData.invoice.invoiceAmount,
              invoiceDate: invoiceData.invoice.invoiceDate,
              paidAmount: invoiceData.invoice.paidAmount,
              comment: invoiceData.invoice.comment,
            },
            recepient: invoiceData.recepient,
            recepientAddress: invoiceData.recepientAddress,
            projectName: invoiceData.projectName,
            projectAddress: invoiceData.projectAddress,
            service: invoiceData.service,
            currentInvoice: isCurrentInvoice,
          };
          await saveLimitedReportInvoice(reportId, invoiceDTO);
        } else {
          const invoiceDTO: CreateMagicReportInvoiceDTO = {
            magicReportId: reportId,
            invoice: {
              number: invoiceData.invoice.number,
              invoiceAmount: invoiceData.invoice.invoiceAmount,
              invoiceDate: invoiceData.invoice.invoiceDate,
              paidAmount: invoiceData.invoice.paidAmount,
              comment: invoiceData.invoice.comment,
            },
            recepient: invoiceData.recepient,
            recepientAddress: invoiceData.recepientAddress,
            projectName: invoiceData.projectName,
            projectAddress: invoiceData.projectAddress,
            service: invoiceData.service,
            currentInvoice: isCurrentInvoice,
          };
          await saveMagicReportInvoice(reportId, invoiceDTO);
        }
      }

      // Refetch report to get updated invoices
      const refreshedReport = isLimitedReport ? await getLimitedReport(reportId) : await getMagicReport(reportId);

      if (refreshedReport?.invoices) {
        const mappedInvoices: ReportInvoiceDTO[] = refreshedReport.invoices.map((inv: any) => ({
          id: inv.id,
          reportId: inv.reportId || inv.limitedReportId || inv.magicReportId || reportId,
          invoice: {
            id: inv.invoice?.id || '',
            number: inv.invoice?.number || '',
            invoiceAmount: inv.invoice?.invoiceAmount || 0,
            paidAmount: inv.invoice?.paidAmount || 0,
            invoiceDate: inv.invoice?.invoiceDate || '',
            comment: inv.invoice?.comment,
            createdOn: inv.invoice?.createdOn,
            updatedOn: inv.invoice?.updatedOn,
          },
          recepient: inv.recepient,
          recepientAddress: inv.recepientAddress,
          projectName: inv.projectName,
          projectAddress: inv.projectAddress,
          service: inv.service,
          currentInvoice: !!inv.currentInvoice,
          createdOn: inv.createdOn,
          updatedOn: inv.updatedOn,
        }));

        setReportState((prev: any) => ({ ...prev, invoices: mappedInvoices }));
        toast.success(
          isUpdatingCurrentInvoice
            ? 'Current invoice updated successfully'
            : isCurrentInvoice
              ? 'Current invoice created successfully'
              : 'Past invoice saved successfully',
        );
      }
    } catch (e: any) {
      console.error('Error saving invoice:', e);
      const errorMessage = e?.response?.data?.errorMessage || e?.response?.data?.message || e?.message || 'Failed to save invoice';
      toast.error(errorMessage);
    }
  };

  const handleDeleteInvoice = (invoiceId: string) => {
    try {
      const nextInvoices = invoices.filter((inv: ReportInvoiceDTO) => inv.id !== invoiceId);
      setReportState((prev: any) => ({ ...prev, invoices: nextInvoices }));
      toast.success('Invoice removed from draft');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete invoice');
    }
  };

  const handleExportInvoice = async (invoiceData: CreateInvoiceDTO) => {
    await exportInvoiceAsPDF(invoiceData, projectData || {}, projectSummary?.projectNumber);
  };

  const handleExportAllInvoices = async (all: ReportInvoiceDTO[]) => {
    await exportAllInvoicesAsPDF(all, projectData || {}, projectSummary?.projectTitle || projectSummary?.projectNumber);
  };

  // Persists edits to an existing invoice (Magic or Limited) and patches local state.
  // The backend records each change in the invoice history.
  const saveInvoiceChanges = useCallback(
    async (invoiceId: string, changes: Partial<EditInvoiceValues>) => {
      const reportId = reportState.reportId || '';
      if (!reportId) return;

      const invoice = invoices.find((inv: ReportInvoiceDTO) => inv.id === invoiceId);
      if (!invoice) return;

      const { changeReason, ...fields } = changes;
      const updatedInvoice = { ...invoice.invoice, ...fields };
      const invoicePayload = {
        number: updatedInvoice.number,
        invoiceAmount: updatedInvoice.invoiceAmount,
        invoiceDate: updatedInvoice.invoiceDate,
        paidAmount: updatedInvoice.paidAmount,
        comment: updatedInvoice.comment,
        changeReason,
      };
      const sharedPayload = {
        invoice: invoicePayload,
        recepient: invoice.recepient,
        recepientAddress: invoice.recepientAddress,
        projectName: invoice.projectName,
        projectAddress: invoice.projectAddress,
        service: invoice.service,
        currentInvoice: invoice.currentInvoice,
      };

      if (reportType === 'limited') {
        const dto: CreateLimitedReportInvoiceDTO = { limitedReportId: reportId, ...sharedPayload };
        await updateLimitedReportCurrentInvoice(reportId, invoiceId, dto);
      } else {
        const dto: CreateMagicReportInvoiceDTO = { magicReportId: reportId, ...sharedPayload };
        await updateMagicReportInvoice(reportId, invoiceId, dto);
      }

      setReportState((prev: any) => ({
        ...prev,
        invoices: prev.invoices.map((inv: ReportInvoiceDTO) => (inv.id === invoiceId ? { ...inv, invoice: updatedInvoice } : inv)),
      }));
    },
    [reportState.reportId, invoices, reportType, setReportState],
  );

  const handleUpdatePaidAmount = useCallback(
    (invoiceId: string, paidAmount: number) => saveInvoiceChanges(invoiceId, { paidAmount }),
    [saveInvoiceChanges],
  );

  // Navigation guard: auto-save current invoice, then warn if past invoices have no paid amount
  useEffect(() => {
    if (!navigationGuardRef) return;

    navigationGuardRef.current = async () => {
      // Auto-save the current invoice form before navigating
      if (invoiceSaveRef.current) {
        const saved = await invoiceSaveRef.current();
        if (!saved) return false;
      }

      const pastInvoices = invoices.filter((inv: ReportInvoiceDTO) => !inv.currentInvoice);
      const someUnpaid =
        pastInvoices.length > 0 && pastInvoices.some((inv: ReportInvoiceDTO) => !inv.invoice.paidAmount || inv.invoice.paidAmount === 0);

      if (!someUnpaid) return true;

      const confirmed = await getConfirmation({
        heading: 'No Invoices Marked as Paid',
        description: 'You indicated that no invoices have been paid since last month. Is that correct?',
        confirmButton: { label: 'Yes, continue', color: 'error' },
        cancelButton: { label: 'Go back', color: 'primary' },
      });
      return confirmed;
    };

    return () => {
      if (navigationGuardRef) navigationGuardRef.current = null;
    };
  }, [invoices, navigationGuardRef, getConfirmation]);

  const currentInvoice = invoices.find((inv: ReportInvoiceDTO) => inv.currentInvoice);
  const canExportAll = showExportAll && !!currentInvoice;

  return (
    <InvoiceRenderer
      invoices={invoices as unknown as BaseInvoiceDTO[]}
      onAddInvoice={handleAddInvoice}
      onDeleteInvoice={handleDeleteInvoice}
      onUpdatePaidAmount={handleUpdatePaidAmount}
      onEditInvoice={saveInvoiceChanges}
      projectData={projectData}
      title={title}
      service={service}
      isMonitoringReport
      currentInvoice={currentInvoice as unknown as BaseInvoiceDTO}
      mode={mode}
      onExportInvoice={handleExportInvoice}
      onExportPastInvoice={handleExportInvoice}
      onExportAllInvoices={canExportAll ? handleExportAllInvoices : undefined}
      saveRef={invoiceSaveRef}
      completeToggle={
        isMagicReport ? (
          <SectionCompleteToggle
            isCompleted={isInvoiceStepCompleted}
            onToggle={handleToggleInvoiceStepCompleted}
            isSaving={isSavingCompletion}
          />
        ) : undefined
      }
    />
  );
};

export default InvoiceStep;
